import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUrlString, useUrlNumber, useUrlDateRange, useClearUrlState } from '../../hooks/useUrlState';
import { Card, Typography, Tag, Button, FilterResetButton, Space, Select, Input, DatePicker, Modal, Textarea, Tooltip, Popconfirm, EntityAvatar, message, StatCard, Counted, cn, CodeChip, entityHue } from '../../ui';
import { productImage } from '../../lib/imageUrls';
import type { TagTone } from '../../ui';
import { Plus, Check, X, Pencil, Download, Package, ClipboardList, Clock, ArrowDownToLine, ArrowUpFromLine, FilterX } from 'lucide-react';
import { RoleBadge, roleLabel, roleTone } from '../../components/RoleBadge';
import { AttachmentCell } from '../../components/AttachmentPreview';
import { AdjustmentDetailModal } from './AdjustmentDetailModal';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission } from '../../store/auth';
import { useWarehouseLookup } from '../../hooks/useWarehouseLookup';
import { useServerSort } from '../../hooks/useServerSort';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { CopyValue } from '../../components/CopyValue';
import { useFocusRow } from '../../hooks/useFocusRow';
import { AdjustmentFormModal, reasonLabel, dotFor } from './AdjustmentFormModal';
import { ExportDialog } from '../../components/ExportDialog';
import { fmtQty, productLabel, batchSuffix } from '../../lib/format';
import { accentForProductName } from '../../lib/productAccent';
import { warehouseOption } from '../../lib/warehouseLabel';
import { PALETTE, TAG_TONE_HUE, filterTint } from '../../lib/palette';
import { formatIst } from '../../lib/time';

const COLOR: Record<string, TagTone> = { DRAFT: 'neutral', SUBMITTED: 'gold', APPROVED: 'green', REJECTED: 'red' };

/** The hue behind the raiser's ROLE tag — `TAG_TONE_HUE` (lib/palette) is the shared tone→colour
 *  map, so a role is the same colour here as on any other filter bar. */
const raiserHue = (raisers: any[] | undefined, id: string): string =>
  TAG_TONE_HUE[roleTone((raisers ?? []).find((x: any) => x.value === id)?.role)] ?? PALETTE.plum[500];

const STATUS_HUE: Record<string, string> = {
  DRAFT: PALETTE.sky[500], SUBMITTED: PALETTE.harvest[500], APPROVED: PALETTE.brand[600], REJECTED: PALETTE.danger[500],
};

/**
 * A product option that says the family name ONCE.
 *
 * The list read "AGN · AGN-2L", "AGN · AGN 500ml", "AGN LTE · AGN LTE 250ml" — the name, a dot, then
 * the same name again with the pack stuck on the end, so the half that actually tells two options
 * apart was buried at the end of a repetition. `productLabel` already knows when one of the two
 * labels contains the other, and `batchSuffix` already strips a known prefix off the longer one; the
 * remainder is the variant, and it becomes a chip of its own per the picker rule.
 */
function productOption(pr: any) {
  const name = String(pr?.name ?? '').trim();
  const sku = String(pr?.sku ?? '').trim();
  const { primary, secondary } = productLabel(name, sku);
  // secondary set → the SKU says something the name does not ("GreenGro Bio" / "AGN-1L").
  // primary !== name → the SKU is the name plus a pack; the pack alone is the news.
  const tail = secondary ?? (primary !== name ? batchSuffix(primary, name) : '');
  const head = secondary || primary !== name ? name || primary : primary;
  const accent = accentForProductName(name || primary);
  return {
    searchLabel: `${name} ${sku}`.trim(),
    title: [name, sku].filter(Boolean).join(' · '),
    label: (
      <span className="flex min-w-0 items-center gap-2">
        <span className="size-2 shrink-0 rounded-full" style={{ background: accent }} aria-hidden />
        <span className="min-w-0 truncate">{head}</span>
        {tail && tail !== head ? <CodeChip hue={accent} className="shrink-0">{tail}</CodeChip> : null}
      </span>
    ),
  };
}

type ReasonSlice = { reason: string; count: number };

/**
 * The reason mix — what a donut chart used to say from its own third of the page.
 *
 * It is a FILTER, not a picture: every reason present is a chip that opens its own slice of the
 * register, so the breakdown and the control that acts on it are the same object. The chips are a
 * partition (every reason, never a top-N slice — the row wraps rather than truncating) and read
 * subject-first, so they visibly add up to the Total tile above them.
 */
function ReasonMix({ slices, active, onPick }: {
  slices: ReasonSlice[];
  active?: string;
  onPick: (reason?: string) => void;
}) {
  const total = slices.reduce((n, s) => n + s.count, 0);
  if (!total) return null;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2.5">
        <span className="shrink-0 type-overline text-muted">Reason mix</span>
        {/* No per-segment tooltip: a slice can be two pixels wide, and the chips below already name
            every reason with its count — the bar carries the PROPORTION, they carry the figures. */}
        <div className="flex h-1.5 min-w-0 flex-1 overflow-hidden rounded-pill bg-surface-3">
          {slices.map((s, i) => (
            <div key={s.reason} className={cn('h-full', dotFor(s.reason, i))} style={{ width: `${(s.count / total) * 100}%` }} />
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {slices.map((s, i) => {
          const on = active === s.reason;
          return (
            <button
              key={s.reason}
              type="button"
              aria-pressed={on}
              onClick={() => onPick(on ? undefined : s.reason)}
              title={on ? 'Showing only this reason — press again to clear' : `Show only ${reasonLabel(s.reason)}`}
              className={cn(
                'inline-flex max-w-full items-center gap-1.5 rounded-pill border px-2 py-1 type-caption transition',
                on
                  ? 'border-brand-600 bg-brand-500/10 shadow-e1'
                  : 'border-line bg-surface hover:border-line-strong hover:bg-surface-2',
              )}
            >
              <span className={cn('size-2 shrink-0 rounded-pill', dotFor(s.reason, i))} />
              <span className="truncate text-content">{reasonLabel(s.reason)}</span>
              <span className="shrink-0 type-caption-strong tabular-nums text-content">{s.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * TURNING A CORRECTION DOWN.
 *
 * This replaced a `Modal.confirm` holding a bare textarea — a yes/no bubble with a box wedged into
 * it, unlabelled, its placeholder wrapping over two lines, and an "OK" button that named neither the
 * act nor its consequence. A rejection is the end of a piece of somebody's work and the only thing
 * they get back is the sentence written here, so it is worth a dialog of its own: what is being
 * turned down, what rejecting does, and a proper field to say why.
 *
 * The reason is REQUIRED. It used to default to the string "Not approved" when left blank, which
 * told the raiser nothing they could act on and left the audit trail a rejection with no grounds.
 */
function RejectAdjustmentModal({ row, warehouse, product, onClose, onSubmit, loading }: {
  row: any;
  warehouse: string;
  product?: { name?: string; sku?: string };
  onClose: () => void;
  onSubmit: (reason: string) => void;
  loading: boolean;
}) {
  const [text, setText] = useState('');
  const why = text.trim();
  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={560}
      title={`Reject ${row.adjustmentNo}`}
      footer={<>
        <Button variant="default" onClick={onClose}>Keep it pending</Button>
        <Button variant="danger" loading={loading} disabled={!why} onClick={() => onSubmit(why)}>
          Reject adjustment
        </Button>
      </>}
    >
      <div className="space-y-3">
        {/* What is being turned down — the reader should not have to remember which row they pressed. */}
        <div className="rounded-card border border-line bg-surface-2 p-3">
          <div className="flex flex-wrap items-center gap-2 type-body-strong text-content">
            <span>{product?.name ?? `#${row.productId}`}</span>
            <Tag tone={row.direction === 'IN' ? 'green' : 'red'}>
              {row.direction} {fmtQty(row.qty)}
            </Tag>
          </div>
          <div className="mt-1 type-caption text-subtle">
            {warehouse} · {reasonLabel(row.reason)}
            {row.raisedByName ? ` · raised by ${row.raisedByName}` : ''}
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface p-3">
          <div className="type-body-strong text-content">Nothing moves.</div>
          <ul className="mt-1.5 list-disc space-y-1 pl-5 type-caption text-subtle">
            <li>No ledger entry is posted — book stock stays exactly as it is now.</li>
            <li>The raiser is notified, and your sentence is kept on the adjustment record.</li>
            <li>Only a Super Admin can put a rejected adjustment back in the queue.</li>
          </ul>
        </div>

        <div>
          <label htmlFor="reject-reason" className="type-caption text-subtle">
            Why is it being rejected? <span className="text-danger-600 dark:text-danger-400">Required</span>
          </label>
          <Textarea
            id="reject-reason" rows={3} className="mt-1" value={text} maxLength={500} autoFocus
            placeholder="e.g. the count was taken before this morning's GRN was posted — recount and raise it again"
            onChange={(e) => setText(e.target.value)}
          />
          <p className="mt-1.5 type-caption text-subtle">
            {row.raisedByName ?? 'The person who raised it'} reads this on the adjustment.
          </p>
        </div>
      </div>
    </Modal>
  );
}

export function AdjustmentsPage({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<any>(null); // a SUBMITTED adjustment being edited
  const [rejecting, setRejecting] = useState<any>(null); // a SUBMITTED adjustment being turned down
  const [viewing, setViewing] = useState<any>(null);     // the row whose full record is open
  /* Page and size live in the URL like every other filter, so Back returns to the page you left and
     a link to page 4 of the register is a link to page 4 of the register. */
  const [page, setPage] = useUrlNumber('page', 1);
  const [pageSize, setPageSize] = useUrlNumber('pageSize', 25);
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  const [reason, setReason] = useUrlString<string | undefined>('reason', undefined);
  // Direction is a filter of its own, so the two quantity tiles can open the rows behind them.
  const [direction, setDirection] = useUrlString<string | undefined>('direction', undefined);
  /* The rest of the register's dimensions. All URL-backed, so a filtered view is a link and Back
     restores it, and ALL of them reach the export unchanged — see baseParams below. */
  const [warehouseId, setWarehouseId] = useUrlString<string | undefined>('warehouseId', undefined);
  const [productId, setProductId] = useUrlString<string | undefined>('productId', undefined);
  const [raisedBy, setRaisedBy] = useUrlString<string | undefined>('raisedBy', undefined);
  const [hasEvidence, setHasEvidence] = useUrlString<string | undefined>('hasEvidence', undefined);
  const [q, setQ] = useUrlString<string | undefined>('q', undefined);
  const [range, setRange] = useUrlDateRange('raised');
  const clearFilters = useClearUrlState();
  const canApprove = usePermission('STOCK.ADJUST.APPROVE');
  const canCreate = usePermission('STOCK.ADJUST.CREATE');
  // Same gate as the page and the endpoint — a shrinkage register carries no pricing, and customers
  // hold none of STOCK.VIEW / STOCK.ADJUST.*.
  const [exportOpen, setExportOpen] = useState(false);
  // Server-side column sort (backend ORDER BY over the full result set, not just this page).
  const { sortState, onSortChange, sort } = useServerSort();

  /* ONE PAGE, COUNTED AND SLICED BY THE SERVER. The browser used to be handed a capped array and
     paginate it itself, so the row count was whatever had arrived and everything past the cap was
     invisible with nothing saying so. `meta.total` is the register's real size. */
  /* ONE object of filters, built once and used by the query, the "is anything filtered" test and
     the export. Three copies of this list would drift, and the copy that drifted would be the one
     that decided which rows left the building. */
  const from = range?.[0]?.format('YYYY-MM-DD');
  const to = range?.[1]?.format('YYYY-MM-DD');
  const filters = useMemo(() => ({
    ...(status ? { status } : {}), ...(reason ? { reason } : {}), ...(direction ? { direction } : {}),
    ...(warehouseId ? { warehouseId } : {}), ...(productId ? { productId } : {}),
    ...(raisedBy ? { raisedBy } : {}), ...(hasEvidence ? { hasEvidence } : {}),
    ...(q?.trim() ? { q: q.trim() } : {}),
    ...(from ? { from } : {}), ...(to ? { to } : {}),
  }), [status, reason, direction, warehouseId, productId, raisedBy, hasEvidence, q, from, to]);

  const { data: paged, isLoading } = useQuery({
    queryKey: ['stock-adjustments', filters, sort, page, pageSize],
    queryFn: () => request<any>({
      url: '/stock-adjustments',
      params: { ...filters, page, pageSize, ...(sort ? { sort } : {}) },
    }),
  });
  const data: any[] = paged?.data ?? [];
  const matched = Number(paged?.meta?.total ?? 0);
  /* An approver arriving from the hub (?focus=…) is looking for ONE correction among the register.
     This list has no expandable row, so the row it names is scrolled to and highlighted. */
  const focus = useFocusRow(data, { match: ['id', 'adjustmentNo'] });
  /* The KPI band and the reason mix are counted in SQL over the WHOLE table. They used to be folded
     in the browser from a SECOND full copy of the list, which inherited its row cap — so past the
     cap every figure above the register quietly stopped counting. Deliberately unfiltered: pressing
     a tile must not rewrite the number that was pressed. */
  const { data: summary } = useQuery({
    queryKey: ['stock-adjustments-summary'],
    queryFn: () => request<any>({ url: '/stock-adjustments/summary' }),
  });
  const stats = {
    total: Number(summary?.total ?? 0),
    submitted: Number(summary?.submitted ?? 0),
    approved: Number(summary?.approved ?? 0),
    rejected: Number(summary?.rejected ?? 0),
    draft: Number(summary?.draft ?? 0),
    inQty: Number(summary?.inQty ?? 0),
    outQty: Number(summary?.outQty ?? 0),
  };
  const slices: ReasonSlice[] = summary?.byReason ?? [];
  const dotIndex = useMemo(() => new Map(slices.map((s, i) => [s.reason, i])), [slices]);
  // Resolve the opaque warehouse/product IDs in the table to human labels. The label map is topped up
  // with the master list so an adjustment against a since-deactivated warehouse still prints its name;
  // the picker itself only ever offers active, in-scope warehouses. See useWarehouseLookup.
  const { labelOf: whLabelOf, byId: whById } = useWarehouseLookup();
  const { data: products } = useQuery({
    queryKey: ['products-list'],
    queryFn: () => requestAllPages('/products'),
  });

  const prodMap = useMemo(() => new Map<string, any>(((products?.data) ?? []).map((p: any) => [String(p.id), p])), [products]);

  /* The warehouses this reader may see, for the picker. A Warehouse Manager gets their own; a senior
     gets all — the same list the New Adjustment form offers, so the two cannot disagree. */
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses-my-adj'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
  });
  /* Raised-by is offered from the people who ACTUALLY appear in this register rather than the whole
     staff list: a filter offering fifty names that match nothing is worse than no filter. */
  const { data: raisers } = useQuery({
    queryKey: ['stock-adjustments-raisers'],
    queryFn: async () => {
      const all = await request<any[]>({ url: '/stock-adjustments', params: { limit: 0 } });
      // The rows already carry the raiser's ROLE; keeping it is what lets the option wear a badge
      // instead of being one more grey name in a list of grey names.
      const by = new Map<string, { name: string; role: string | null }>();
      for (const r of all ?? []) {
        if (!r.createdBy) continue;
        const k = String(r.createdBy);
        const prev = by.get(k);
        by.set(k, { name: r.raisedByName ?? `#${r.createdBy}`, role: r.raisedByRole ?? prev?.role ?? null });
      }
      return [...by.entries()]
        .map(([value, u]) => ({
          value,
          name: u.name,
          role: u.role,
          // Plain text for the search box and for the collapsed trigger's title.
          searchLabel: `${u.name} ${u.role ? roleLabel(u.role) : ''}`.trim(),
          title: u.name,
          label: (
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="min-w-0 truncate">{u.name}</span>
              {/* Never a pill that just repeats the name beside it — same guard the rows use. */}
              {u.role && roleLabel(u.role) !== u.name && <RoleBadge role={u.role} />}
            </span>
          ),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  // A decision changes both the rows and every figure counted over them.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['stock-adjustments'] });
    qc.invalidateQueries({ queryKey: ['stock-adjustments-summary'] });
  };

  const approve = useMutation({
    mutationFn: (id: string) => request({ url: `/stock-adjustments/${id}/approve`, method: 'POST' }),
    onSuccess: () => { message.success('Approved'); setViewing(null); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Approval failed'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      request({ url: `/stock-adjustments/${id}/reject`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Rejected'); setRejecting(null); setViewing(null); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  /* Every tile drills, and the pair it writes is the EXACT slice its figure was counted over — the
     quantity tiles count POSTED movement, so they open APPROVED rows of that direction rather than
     every IN row ever raised. Both writes survive: useUrlState coalesces them into one update. */
  const drill = (next: { status?: string; direction?: string }) => {
    setStatus(next.status);
    setDirection(next.direction);
    setPage(1); // page 4 of the old filter is not page 4 of the new one
  };
  const pickReason = (r?: string) => { setReason(r); setPage(1); };
  const on = (s?: string, d?: string) => status === s && direction === d;
  const FILTER_KEYS = ['status', 'reason', 'direction', 'warehouseId', 'productId', 'raisedBy', 'hasEvidence', 'q', 'raised', 'page'];
  const filtered = Object.keys(filters).length > 0;
  // A gap named rather than left out: the status tiles cover SUBMITTED + APPROVED, so anything sitting
  // in the other two states has to be said somewhere or Total stops adding up.
  const otherStates = [
    stats.rejected ? `${stats.rejected} rejected` : null,
    stats.draft ? `${stats.draft} draft` : null,
  ].filter(Boolean).join(' · ');

  const columns: DataTableColumn<any>[] = [
    {
      // Doc number + who raised it. Folded into the existing cell rather than added as a column: a
      // write-off register that names nobody cannot be checked, and a table only has so much width.
      key: 'adjustmentNo', title: 'Adj No', dataIndex: 'adjustmentNo', sortable: true, alwaysVisible: true, width: 200,
      render: (v: string, r: any) => (
        <span className="block min-w-0">
          <CopyValue value={v} label="Adjustment no" truncate>
            <span className="type-body-strong text-content">{v}</span>
          </CopyValue>
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate type-caption text-subtle" title={r.raisedByName ?? undefined}>
              {r.raisedByName ? `by ${r.raisedByName}` : 'raiser not recorded'}
            </span>
            {/* Never a pill that just repeats the name beside it. */}
            {roleLabel(r.raisedByRole ?? '') !== (r.raisedByName ?? '') && (
              <RoleBadge role={r.raisedByRole} className="scale-90 origin-left" />
            )}
          </span>
        </span>
      ),
    },
    {
      // Warehouse / Product render joined names (not the raw id they'd sort by) — not server-sortable.
      key: 'warehouseId', title: 'Warehouse', dataIndex: 'warehouseId', width: 220, ellipsis: true,
      render: (v: string) => whLabelOf(v),
    },
    {
      // Declared NARROW on purpose: under `layoutFixed` the leftover width is handed out in
      // proportion to these numbers, so the widest declaration soaks the most empty space. The
      // product name is short; the reason and the warehouse are the ones that can use the room.
      key: 'productId', title: 'Product', dataIndex: 'productId', width: 190,
      render: (v: string) => {
        const p = prodMap.get(String(v));
        if (!p) return v;
        const img = productImage(p);
        return (
          <span className="flex min-w-0 items-center gap-2.5" onClick={(e) => e.stopPropagation()}>
            {/* The pack shot is a thumbnail you can OPEN — same viewer the evidence uses. */}
            {img
              ? <AttachmentCell files={[img]} label={p.name} size={32} />
              : <EntityAvatar name={p.name} square size={32} icon={<Package size={14} />} />}
            <span className="min-w-0">
              <span className="block truncate text-content" title={p.name}>{p.name}</span>
              <span className="block truncate type-caption text-subtle">{p.sku}</span>
            </span>
          </span>
        );
      },
    },
    {
      // The lot's own number — "#2163" is a primary key and identifies nothing on a shelf.
      key: 'batchId', title: 'Batch', dataIndex: 'batchId', defaultVisible: false, width: 150,
      render: (_: any, r: any) => (r.batchNo
        ? <CopyValue value={r.batchNo} label="Batch no" truncate><span className="font-mono">{r.batchNo}</span></CopyValue>
        : <span className="text-subtle">{r.direction === 'IN' ? 'ADJ- lot' : 'FIFO'}</span>),
    },
    {
      /*
       * QUANTITY AND DIRECTION ARE ONE FACT, SO THEY ARE ONE COLUMN.
       *
       * Split across two, a 24px IN/OUT pill sat alone in its own column and the number was pinned
       * to the far right of another — between the product name and the figure lay ~270px of nothing,
       * which is what a reader kept stopping on. Together they read as the movement itself: a signed,
       * coloured figure with the direction beside it, starting immediately after the product.
       *
       * `qty` arrives as a DECIMAL(14,3) string, so printing it raw put "2.000" on every whole-number
       * row; fmtQty drops the trailing zeros and keeps a real fraction. Sorting stays on the quantity,
       * and direction is still filtered from the toolbar and from the two quantity KPI tiles.
       */
      key: 'qty', title: 'Quantity', dataIndex: 'qty', sortable: true, width: 130,
      render: (v: any, r: any) => {
        const isIn = r.direction === 'IN';
        return (
          <span className="flex items-center gap-2">
            <span className={cn('tabular-nums type-body-strong', isIn ? 'text-brand-700 dark:text-brand-300' : 'text-danger-600 dark:text-danger-500')}>
              {isIn ? '+' : '−'}{fmtQty(v)}
            </span>
            <Tag tone={isIn ? 'green' : 'red'}>{r.direction}</Tag>
          </span>
        );
      },
    },
    {
      // Widest of the value columns: the reason IS the subject of a write-off register, and at the
      // width it inherited it read "Free sa…" on every row.
      key: 'reason', title: 'Reason', dataIndex: 'reason', width: 300, ellipsis: true,
      // The same words and the same colour as the mix strip above the rows, so one reads as the other's key.
      render: (v: string) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className={cn('size-2 shrink-0 rounded-pill', dotFor(v, dotIndex.get(v) ?? 0))} />
          <span className="truncate" title={reasonLabel(v)}>{reasonLabel(v)}</span>
        </span>
      ),
    },
    { key: 'notes', title: 'Notes', dataIndex: 'notes', defaultVisible: false, ellipsis: true, width: 220,
      render: (v: string) => v ? <span title={v}>{v}</span> : <span className="text-subtle">—</span> },
    {
      key: 'evidenceUrl', title: 'Evidence', dataIndex: 'evidenceUrl', defaultVisible: false, width: 130,
      render: (_: any, r: any) => {
        const files = (r.evidenceUrls?.length ? r.evidenceUrls : [r.evidenceUrl]).filter(Boolean);
        return files.length
          ? <span onClick={(e) => e.stopPropagation()}><AttachmentCell files={files} label={`Evidence for ${r.adjustmentNo}`} /></span>
          : <span className="text-subtle">—</span>;
      },
    },
    {
      // Status + who decided it — the other half of the accountability the register was missing.
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 180,
      render: (s: string, r: any) => (
        <span className="block min-w-0">
          <Tag tone={COLOR[s]}>{s}</Tag>
          {(s === 'APPROVED' || s === 'REJECTED') && (
            <span className="mt-0.5 flex min-w-0 items-center gap-1">
              <span className="truncate type-caption text-subtle" title={r.decidedByName ?? undefined}>
                {r.decidedByName ? `by ${r.decidedByName}` : 'decider not recorded'}
              </span>
              {roleLabel(r.decidedByRole ?? '') !== (r.decidedByName ?? '') && (
                <RoleBadge role={r.decidedByRole} className="scale-90 origin-left" />
              )}
            </span>
          )}
        </span>
      ),
    },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, defaultVisible: false, width: 170, render: (v: string) => formatIst(v) },
    ...(canApprove || canCreate ? [{
      /*
       * Icon-only 32px actions on ONE line, in the app's fixed order (move-forward → destructive).
       *
       * Three labelled buttons ran the column to 240px of repeated chrome and wrapped Reject onto a
       * second line, which pushed every row taller than its own data. Intent is carried by the tint
       * instead: blue = amend, green = the forward step, red = destructive — a coloured glyph on its
       * own soft background, never a solid fill. Each one keeps a tooltip AND an aria-label, because
       * on an icon-only button the aria-label is the entire accessible name.
       */
      key: 'action', title: 'Action', alwaysVisible: true, width: 120, align: 'right',
      render: (_: any, r: any) =>
        r.status === 'SUBMITTED' ? (
          <div className="flex flex-nowrap items-center justify-end gap-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
            {canCreate && (
              <Tooltip label="Edit this adjustment">
                <Button size="sm" variant="soft-info" className="shrink-0" aria-label="Edit adjustment"
                  icon={<Pencil className="size-4" />} onClick={() => setEditing(r)} />
              </Tooltip>
            )}
            {canApprove && (
              <>
                <Popconfirm
                  title="Approve adjustment?"
                  description="This posts the stock movement to the ledger and cannot be undone."
                  okText="Approve"
                  onConfirm={() => approve.mutate(r.id)}
                >
                  <Tooltip label="Approve — posts the stock movement to the ledger">
                    <Button size="sm" variant="ghost" className="shrink-0" aria-label="Approve adjustment"
                      icon={<Check className="size-4" />} loading={approve.isPending && approve.variables === r.id} />
                  </Tooltip>
                </Popconfirm>
                <Tooltip label="Reject — nothing moves, and the reason is recorded">
                  <Button size="sm" variant="soft-danger" className="shrink-0" aria-label="Reject adjustment"
                    icon={<X className="size-4" />} onClick={() => setRejecting(r)} />
                </Tooltip>
              </>
            )}
          </div>
        ) : null,
    } as DataTableColumn<any>] : []),
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Stock Adjustments</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button size="sm" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
          {canCreate && !embedded ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setShowNew(true)}>
              New Adjustment
            </Button>
          ) : undefined}
        </div>
      </div>

      {/*
        KPI band — the shared ui/StatCard, so it carries the same tone wash, icon chip and elevation
        as every other register's band. Every tile OPENS the slice of the register it counted, and
        lights up while that slice is the one on screen.

        THE WORDING WAS AMBIGUOUS IN A WAY THAT MATTERED. "Total 62" and "Total IN qty 836" sat side
        by side counting two different THINGS — 62 adjustments, 836 units — and neither said which,
        so the band read as one series of numbers that refuse to reconcile. Every value now carries
        its own unit. The last two also never said their scope: they are APPROVED-only (that is where
        they drill to), so an unapproved write-off is in the 62 and not in the 840; the hint says so
        now. And "Total IN qty" was the column name, not the fact — the label states what actually
        happened to the stock, which is what a reader came for.
      */}
      <div className="mb-3 grid gap-3 grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Adjustments raised" value={<Counted n={stats.total} one="adjustment" many="adjustments" />}
          tone="neutral" icon={<ClipboardList className="size-4" />}
          hint={otherStates || undefined}
          active={on(undefined, undefined)} onClick={() => drill({})}
        />
        <StatCard
          label="Waiting for approval" value={<Counted n={stats.submitted} one="adjustment" many="adjustments" />}
          tone="gold" icon={<Clock className="size-4" />}
          hint={stats.submitted ? 'waiting on an approver' : 'nothing waiting'}
          active={on('SUBMITTED', undefined)} onClick={() => drill({ status: 'SUBMITTED' })}
        />
        <StatCard
          label="Approved" value={<Counted n={stats.approved} one="adjustment" many="adjustments" />}
          tone="brand" icon={<Check className="size-4" />}
          hint="posted to the ledger"
          active={on('APPROVED', undefined)} onClick={() => drill({ status: 'APPROVED' })}
        />
        <StatCard
          label="Added back to stock" value={<Counted n={stats.inQty} one="unit" many="units" />}
          tone="sky" icon={<ArrowDownToLine className="size-4" />}
          hint="on approved adjustments"
          active={on('APPROVED', 'IN')} onClick={() => drill({ status: 'APPROVED', direction: 'IN' })}
        />
        <StatCard
          label="Written off stock" value={<Counted n={stats.outQty} one="unit" many="units" />}
          tone="danger" icon={<ArrowUpFromLine className="size-4" />}
          hint="on approved adjustments"
          active={on('APPROVED', 'OUT')} onClick={() => drill({ status: 'APPROVED', direction: 'OUT' })}
        />
      </div>

      {/* One full-width register. The reason breakdown rides inside its toolbar — it IS the reason
          filter, so it belongs with the other filters rather than in a card of its own. */}
      <Card>
        <DataTable
          storageKey="adjustments"
          rowKey="id"
          loading={isLoading}
          /* Every column carries a width and the leftover is shared out proportionally. Without
             this the slack all landed on the one width-less column and left a canyon between
             PRODUCT and QTY. */
          layoutFixed
          dataSource={data ?? []}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          rowClassName={focus.rowClassName}
          onRow={(r) => ({ onClick: () => setViewing(r), className: 'cursor-pointer' })}
          /* Server-side: the page, its size and the TOTAL are all the backend's answer. */
          pagination={{
            current: page,
            pageSize,
            total: matched,
            showSizeChanger: true,
            // 25 is this register's natural page; the kit folds a size outside this list in anyway.
            pageSizeOptions: [10, 25, 50, 100],
            showTotal: (t, [from, to]) => `${from}-${to} of ${t.toLocaleString('en-IN')}`,
            onChange: (p, ps) => { setPage(ps === pageSize ? p : 1); setPageSize(ps); },
          }}
          toolbarLeft={
            /* Filters in COLUMN order, so the bar reads like the table under it: what it is (search),
               where (warehouse), what (product), how much (direction), why (reason — the chips
               below), who (raised by), when (raised on), and the two states. */
            <Space wrap>
              <Input.Search
                allowClear
                placeholder="Adj no or note"
                style={{ width: 200, ...(q?.trim() ? filterTint(PALETTE.sky[500]) : undefined) }}
                defaultValue={q}
                onSearch={(v: string) => { setQ(v || undefined); setPage(1); }}
              />
              {/* The warehouse option is the app-wide one — its code as a chip in that warehouse's
                  own identity hue, the site name beside it — so a warehouse is the same colour here,
                  in the stock table and beside every avatar. */}
              <Select
                placeholder="Warehouse" allowClear showSearch optionFilterProp="searchLabel"
                style={{ width: 210, ...filterTint(warehouseId ? entityHue(whById.get(String(warehouseId))?.code ?? '') : null) }}
                value={warehouseId}
                onChange={(v: any) => { setWarehouseId(v); setPage(1); }}
                options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), ...warehouseOption(w) }))}
              />
              <Select
                placeholder="Product" allowClear showSearch optionFilterProp="searchLabel"
                style={{ width: 210, ...filterTint(productId ? accentForProductName(prodMap.get(String(productId))?.name) : null) }}
                value={productId}
                onChange={(v: any) => { setProductId(v); setPage(1); }}
                options={((products?.data) ?? []).map((pr: any) => ({ value: String(pr.id), ...productOption(pr) }))}
              />
              {/* Direction and status are the two filters whose VALUE already has a colour everywhere
                  else on this page — the IN/OUT arrows in the table, the status tag on every row. The
                  option is that same tag, so choosing one shows you the thing you will be looking at. */}
              <Select
                placeholder="Direction" allowClear value={direction}
                style={{ width: 140, ...filterTint(direction ? (direction === 'IN' ? PALETTE.brand[600] : PALETTE.danger[500]) : null) }}
                onChange={(v: any) => { setDirection(v); setPage(1); }}
                options={[
                  { value: 'IN', title: 'IN +', label: <Tag tone="green">IN&nbsp;+</Tag> },
                  { value: 'OUT', title: 'OUT −', label: <Tag tone="red">OUT&nbsp;−</Tag> },
                ]}
              />
              <Select
                placeholder="Status" allowClear value={status}
                style={{ width: 150, ...filterTint(status ? STATUS_HUE[status] : null) }}
                onChange={(v: any) => { setStatus(v); setPage(1); }}
                options={Object.keys(COLOR).map((s) => ({ value: s, title: s, label: <Tag tone={COLOR[s]}>{s}</Tag> }))}
              />
              <Select
                placeholder="Raised by" allowClear showSearch optionFilterProp="label"
                style={{ width: 205, ...filterTint(raisedBy ? raiserHue(raisers, raisedBy) : null) }}
                value={raisedBy}
                onChange={(v: any) => { setRaisedBy(v); setPage(1); }}
                options={raisers ?? []}
              />
              {/* Evidence is a yes/no about whether the claim can be checked, so it is coloured like
                  one — not two identical grey lines of text. */}
              <Select
                placeholder="Evidence" allowClear value={hasEvidence}
                style={{ width: 165, ...filterTint(hasEvidence ? (hasEvidence === 'yes' ? PALETTE.brand[600] : PALETTE.danger[500]) : null) }}
                onChange={(v: any) => { setHasEvidence(v); setPage(1); }}
                options={[
                  { value: 'yes', title: 'Has evidence', label: <Tag tone="green">Has evidence</Tag> },
                  { value: 'no', title: 'No evidence', label: <Tag tone="red">No evidence</Tag> },
                ]}
              />
              <DatePicker.RangePicker
                value={range as any}
                style={(from || to) ? filterTint(PALETTE.sky[500]) : undefined}
                onChange={(v: any) => { setRange(v ?? [null, null]); setPage(1); }}
                placeholder={['Raised from', 'Raised to'] as any}
              />
              {filtered && (
                <FilterResetButton size="md" onClick={() => clearFilters(FILTER_KEYS)} />
              )}
            </Space>
          }
          toolbarBelow={
            <div className="flex flex-col gap-2">
              <ReasonMix slices={slices} active={reason} onPick={pickReason} />
              {filtered && (
                /* WHY THE ROWS AND THE TILES DISAGREE.
                   The KPI band deliberately counts the WHOLE register, so a filtered list sitting
                   under a Total of 47 read as 41 lost rows. Nothing on the page said a filter was
                   on. It does now — in the filter's own colour, naming what is applied, next to the
                   control that clears it. */
                <div className="flex flex-wrap items-center gap-2 rounded-control border border-harvest-400/40 bg-harvest-400/[0.10] px-2.5 py-1.5">
                  <span className="type-caption-strong text-content">
                    Showing {matched.toLocaleString('en-IN')} of {stats.total.toLocaleString('en-IN')}
                  </span>
                  <span className="type-caption text-subtle">filtered by</span>
                  {status && <Tag tone={COLOR[status]}>{status}</Tag>}
                  {direction && <Tag tone={direction === 'IN' ? 'green' : 'red'}>{direction}</Tag>}
                  {reason && <Tag tone="gold">{reasonLabel(reason)}</Tag>}
                  {warehouseId && <CodeChip hue={entityHue(whById.get(String(warehouseId))?.code ?? '')}>{whLabelOf(warehouseId)}</CodeChip>}
                  {productId && <CodeChip hue={accentForProductName(prodMap.get(String(productId))?.name)}>{prodMap.get(String(productId))?.name ?? `#${productId}`}</CodeChip>}
                  {raisedBy && (() => {
                    const u = (raisers ?? []).find((x: any) => x.value === raisedBy);
                    return <Tag tone={roleTone(u?.role)}>{u?.name ?? 'raiser'}</Tag>;
                  })()}
                  {hasEvidence && <Tag tone={hasEvidence === 'yes' ? 'green' : 'red'}>{hasEvidence === 'yes' ? 'has evidence' : 'no evidence'}</Tag>}
                  {(from || to) && <Tag tone="neutral">{from ?? '…'} → {to ?? '…'}</Tag>}
                  {q?.trim() && <Tag tone="neutral">“{q.trim()}”</Tag>}
                  <Button size="sm" variant="text" className="ms-auto" icon={<FilterX className="size-4" />}
                    onClick={() => clearFilters(FILTER_KEYS)}>
                    Show all {stats.total.toLocaleString('en-IN')}
                  </Button>
                </div>
              )}
            </div>
          }
        />
      </Card>

      {viewing && (
        <AdjustmentDetailModal
          row={viewing}
          product={prodMap.get(String(viewing.productId))}
          warehouse={whLabelOf(viewing.warehouseId)}
          canApprove={canApprove}
          canCreate={canCreate}
          approving={approve.isPending && approve.variables === viewing.id}
          onClose={() => setViewing(null)}
          // Hand off rather than stack: a second dialog over the first buries it behind another
          // backdrop and leaves two Escape targets.
          onEdit={() => { setEditing(viewing); setViewing(null); }}
          onReject={() => { setRejecting(viewing); setViewing(null); }}
          onApprove={() => approve.mutate(viewing.id)}
        />
      )}
      <AdjustmentFormModal open={showNew || !!editing} editing={editing} onClose={() => { setShowNew(false); setEditing(null); }} />
      {rejecting && (
        <RejectAdjustmentModal
          row={rejecting}
          warehouse={whLabelOf(rejecting.warehouseId)}
          product={prodMap.get(String(rejecting.productId))}
          loading={reject.isPending}
          onClose={() => setRejecting(null)}
          onSubmit={(reason) => reject.mutate({ id: rejecting.id, reason })}
        />
      )}
      {/* No period picker: the adjustment register is filtered by status, direction and reason, not by
          date — the question it answers is "what is written off and what is awaiting approval", not "when". */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export stock adjustments"
        description="Exactly the rows on screen — every filter you have set is carried into the file, and the file says which ones."
        baseUrl="/stock-adjustments/export"
        /* THE SAME `filters` OBJECT THE TABLE IS READING. An export that widened the selection would
           be the worst kind of bug in a write-off register, so there is only one list. */
        baseParams={{ ...filters, ...(sort ? { sort } : {}) }}
        /* PDF first, so the primary button is Preview rather than Download — a register is something
           you look at before you decide to keep a copy. */
        formats={['pdf', 'xlsx']}
        hidePeriod
      />
    </div>
  );
}
